import React, { Suspense, useEffect, useRef, useState, lazy } from "react";
import { ArrowRight, Compass } from "lucide-react";
import { useApp } from "@/store/AppContext";
import CountUp from "@/components/CountUp";

const Globe = lazy(() => import("react-globe.gl"));

const ARCS = [
  { startLat: 6.5, startLng: 3.4, endLat: 51.5, endLng: -0.12 },
  { startLat: 25.2, startLng: 55.3, endLat: 43.6, endLng: -79.4 },
  { startLat: 28.6, startLng: 77.2, endLat: -33.8, endLng: 151.2 },
  { startLat: 30.0, startLng: 31.2, endLat: 52.5, endLng: 13.4 },
  { startLat: -1.3, startLng: 36.8, endLat: 1.35, endLng: 103.8 },
  { startLat: 24.7, startLng: 46.7, endLat: 25.2, endLng: 55.3 },
];

function InteractiveGlobe({ theme, size }) {
  const ref = useRef();

  useEffect(() => {
    if (ref.current) {
      const controls = ref.current.controls();
      controls.autoRotate = true;
      controls.autoRotateSpeed = 0.6;
      controls.enableZoom = false;
      ref.current.pointOfView({ lat: 15, lng: 30, altitude: 2.2 });
    }
  }, []);

  const globeImage = theme === "dark"
    ? "//unpkg.com/three-globe/example/img/earth-night.jpg"
    : "//unpkg.com/three-globe/example/img/earth-blue-marble.jpg";

  return (
    <Globe
      ref={ref}
      width={size}
      height={size}
      backgroundColor="rgba(0,0,0,0)"
      globeImageUrl={globeImage}
      bumpImageUrl="//unpkg.com/three-globe/example/img/earth-topology.png"
      showAtmosphere
      atmosphereColor={theme === "dark" ? "#C9A961" : "#4B84D6"}
      atmosphereAltitude={0.22}
      arcsData={ARCS}
      arcColor={() => theme === "dark"
        ? ["rgba(201,169,97,0.1)", "rgba(201,169,97,0.9)"]
        : ["rgba(11,30,63,0.1)", "rgba(11,30,63,0.85)"]}
      arcDashLength={0.5}
      arcDashGap={0.6}
      arcDashAnimateTime={3500}
      arcStroke={0.35}
    />
  );
}

function StatItem({ target, suffix, label, testId, accent }) {
  return (
    <div data-testid={testId} className="flex flex-col">
      <div className="font-serif text-4xl sm:text-5xl leading-none text-app">
        <CountUp target={target} suffix="" />
        {suffix && <span style={{ color: accent }}>{suffix}</span>}
      </div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-app-muted mt-2">{label}</div>
    </div>
  );
}

export default function Hero() {
  const { theme, openContact } = useApp();
  const [size, setSize] = useState(600);
  const [scroll, setScroll] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const compute = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      const w = mobile
        ? Math.min(window.innerWidth * 0.85, 380)
        : Math.min(window.innerWidth * 0.5, 640);
      setSize(w);
    };
    compute();
    window.addEventListener("resize", compute);

    const onScroll = () => setScroll(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  // Globe zoom-in-and-fade based on scroll (first 600px window)
  const progress = Math.min(scroll / 600, 1);
  const globeScale = 1 + progress * 1.6;
  const globeOpacity = 1 - progress * 1.25;

  const accent = theme === "dark" ? "#C9A961" : "#0B1E3F";

  return (
    <>
      {/* -------- DESKTOP HERO -------- */}
      <section
        data-testid="hero-desktop"
        className="hidden md:block relative overflow-hidden hero-bg pt-24 pb-16"
      >
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-12 gap-6 items-center min-h-[calc(100vh-6rem)]">
          <div className="col-span-6 relative z-10 space-y-8 fade-up">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] font-semibold" style={{ color: accent }}>
              <Compass className="w-4 h-4" /> Global Immigration Partners
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight text-app">
              Connecting people to <br />
              <em style={{ color: accent }}>their future.</em>
            </h1>
            <p className="text-base text-app-muted max-w-lg leading-relaxed">
              We guide ambitious talent across borders — from first inquiry to visa
              approval — with a discreet, senior-led immigration practice.
            </p>

            {/* Consolidated stats row */}
            <div className="grid grid-cols-4 gap-6 border-y border-[var(--card-border)] py-6 max-w-2xl fade-up fade-up-d1">
              <StatItem testId="stat-950" target={950} suffix="+" label="Approved in 5 yrs" accent={accent} />
              <StatItem testId="stat-46" target={46} label="Countries" accent={accent} />
              <StatItem testId="stat-98" target={98} suffix="%" label="Success rate" accent={accent} />
              <StatItem testId="stat-5" target={5} suffix="+" label="Years experience" accent={accent} />
            </div>

            <div className="flex flex-wrap gap-3 fade-up fade-up-d2">
              <button
                data-testid="hero-start-application-btn"
                onClick={() => openContact()}
                className="rounded-full px-7 py-3.5 text-sm font-semibold bg-[#0B1E3F] text-white hover:bg-[#162A4D] transition inline-flex items-center gap-2"
              >
                Start my application <ArrowRight className="w-4 h-4" />
              </button>
              <button
                data-testid="hero-see-destinations-btn"
                onClick={() => document.getElementById("destinations")?.scrollIntoView({ behavior: "smooth" })}
                className="rounded-full px-7 py-3.5 text-sm font-semibold bg-[#0B1E3F] text-white hover:bg-[#162A4D] transition inline-flex items-center gap-2"
              >
                See destinations <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div
            className="col-span-6 relative flex items-center justify-center globe-wrap"
            style={{
              transform: `scale(${globeScale})`,
              opacity: Math.max(globeOpacity, 0),
              transition: "transform 0.1s linear, opacity 0.1s linear",
            }}
          >
            <div className="absolute inset-0 pointer-events-none rounded-full blur-3xl"
                 style={{ background: theme === "dark" ? "rgba(201,169,97,0.1)" : "rgba(75,132,214,0.15)" }} />
            {!isMobile && (
              <Suspense
                fallback={<div className="w-[520px] h-[520px] rounded-full border border-[var(--card-border)] spin-slow" />}
              >
                <InteractiveGlobe theme={theme} size={size} />
              </Suspense>
            )}
          </div>
        </div>
      </section>

      {/* -------- MOBILE HERO -------- */}
      <section
        data-testid="hero-mobile"
        className="md:hidden relative overflow-hidden hero-bg pt-24 pb-10"
      >
        <div className="relative px-6 flex flex-col items-center text-center">
          <div
            className="relative w-[75vw] h-[75vw] max-w-[320px] max-h-[320px] mx-auto"
            style={{
              transform: `scale(${globeScale})`,
              opacity: Math.max(globeOpacity, 0),
              transition: "transform 0.1s linear, opacity 0.1s linear",
            }}
          >
            {isMobile && (
              <Suspense fallback={<div className="w-full h-full rounded-full bg-[var(--bg-secondary)] spin-slow" />}>
                <InteractiveGlobe theme={theme} size={size} />
              </Suspense>
            )}
          </div>

          <div className="relative mt-4 space-y-4 z-10 max-w-md">
            <h1 className="font-serif text-3xl leading-[1.1] tracking-tight text-app">
              Connecting people to <br />
              <em style={{ color: accent }}>their future.</em>
            </h1>
            <p className="text-sm text-app-muted leading-relaxed px-4">
              Visa applications approved across 46 countries with a 98% success rate.
            </p>

            {/* Consolidated stats — mobile */}
            <div className="grid grid-cols-4 gap-2 border-y border-[var(--card-border)] py-4 my-4">
              {[
                [950, "+", "Approved"],
                [46, "", "Countries"],
                [98, "%", "Success"],
                [5, "+", "Years"],
              ].map(([v, s, l]) => (
                <div key={l} className="flex flex-col">
                  <div className="font-serif text-2xl leading-none text-app">
                    <CountUp target={v} />
                    <span style={{ color: accent }}>{s}</span>
                  </div>
                  <div className="text-[9px] uppercase tracking-widest text-app-muted mt-1">{l}</div>
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-3 pt-2 px-4">
              <button
                data-testid="hero-mobile-start-btn"
                onClick={() => openContact()}
                className="w-full rounded-full py-3.5 bg-[#0B1E3F] text-white font-semibold text-sm shadow-lg"
              >
                Start my application
              </button>
              <button
                data-testid="hero-mobile-destinations-btn"
                onClick={() => document.getElementById("destinations")?.scrollIntoView({ behavior: "smooth" })}
                className="w-full rounded-full py-3.5 bg-[#0B1E3F] text-white font-semibold text-sm shadow-lg"
              >
                See destinations
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
